import React, { useRef } from 'react';
import { Pressable, View } from 'react-native';

import { HStack, Text, VStack } from '@squire/ui';

import { AppleCalendarIcon, GoogleCalendarIcon } from '@/components/_icons';
import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { useToast } from '@/components/_shared/Toaster';
import { useOnMount } from '@/hooks/shared/useOnMount';
import { useErrorTranslations } from '@/hooks/useErrorTranslations';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  type CalendarEventData,
  openAppleCalendar,
  openGoogleCalendar,
  showAppleCalendar,
  showGoogleCalendar,
} from '@/utils/calendarUtils';

import { styles } from './styles';
import type { CalendarSelectionModalProps } from './types';

export const CalendarSelectionModal: React.FC<CalendarSelectionModalProps> = ({
  title,
  startDate,
  endDate,
  location,
  description,
  confirmationCode,
  onClose,
}) => {
  const { t } = useScreenTranslations('CalendarSelectionModal');
  const { t: errorT } = useErrorTranslations('calendar');
  const { t: appointmentDetailT } = useScreenTranslations(
    'AppointmentDetailInfo',
  );
  const { toast } = useToast();
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  // Generate translated confirmation code text if confirmationCode is provided
  const confirmationCodeText = confirmationCode
    ? appointmentDetailT('calendarDescription.confirmationCode', {
        confirmationCode,
      })
    : undefined;

  const eventData: CalendarEventData = {
    title,
    startDate,
    endDate,
    location,
    description,
    confirmationCode,
    confirmationCodeText,
  };

  const handleGoogleCalendar = async () => {
    const result = await openGoogleCalendar(eventData);

    if (result.success) {
      onClose();
    } else {
      toast({
        variant: 'error',
        message: errorT('googleCalendarFailed'),
        description: errorT(result.errorKey),
      });
    }
  };

  const handleAppleCalendar = async () => {
    const result = await openAppleCalendar(eventData);

    if (result.success) {
      onClose();
    } else {
      toast({
        variant: 'error',
        message: errorT('appleCalendarFailed'),
        description: errorT(result.errorKey),
      });
    }
  };

  // Open the bottom sheet immediately on mount
  useOnMount(() => {
    bottomSheetRef.current?.open();
  });

  return (
    <BottomSheet ref={bottomSheetRef} onDismiss={onClose} dismissible>
      <VStack style={styles.content}>
        {/* Header */}
        <HStack style={styles.header}>
          <VStack style={styles.titleContainer}>
            <Text variant="3xlStrong" color="textPrimary">
              {t('title')}
            </Text>
          </VStack>
          <CloseButton
            onPress={onClose}
            accessibilityLabel={t('accessibility.close')}
            accessibilityHint={t('accessibility.closeHint')}
          />
        </HStack>

        {/* Calendar Options */}
        <HStack style={styles.calendarOptions}>
          {/* Google Calendar */}
          {showGoogleCalendar() && (
            <Pressable
              onPress={handleGoogleCalendar}
              style={styles.calendarOption}
              accessibilityRole="button"
              accessibilityLabel={t('accessibility.googleCalendar')}
            >
              <View style={styles.calendarIconContainer}>
                <GoogleCalendarIcon size={64} style={styles.calendarIcon} />
              </View>
              <Text
                variant="m"
                color="textPrimary"
                style={styles.calendarLabel}
              >
                {t('googleCalendar')}
              </Text>
            </Pressable>
          )}

          {/* Apple Calendar */}
          {showAppleCalendar() && (
            <Pressable
              onPress={handleAppleCalendar}
              style={styles.calendarOption}
              accessibilityRole="button"
              accessibilityLabel={t('accessibility.appleCalendar')}
            >
              <View style={styles.calendarIconContainer}>
                <AppleCalendarIcon size={64} style={styles.calendarIcon} />
              </View>
              <Text
                variant="m"
                color="textPrimary"
                style={styles.calendarLabel}
              >
                {t('appleCalendar')}
              </Text>
            </Pressable>
          )}
        </HStack>
      </VStack>
    </BottomSheet>
  );
};

CalendarSelectionModal.displayName = 'CalendarSelectionModal';
